import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ShieldCheck, CheckCircle, XCircle, Clock } from "@phosphor-icons/react";

const STATUS_STYLE = {
  pending: "border-[var(--vs-warning)]/40 text-[var(--vs-warning)] bg-[var(--vs-warning)]/10",
  approved: "border-[var(--vs-success)]/40 text-[var(--vs-success)] bg-[var(--vs-success)]/10",
  rejected: "border-[var(--vs-danger)]/40 text-[var(--vs-danger)] bg-[var(--vs-danger)]/10",
};

export default function Approvals() {
  const { user } = useAuth();
  const [tab, setTab] = useState("pending");
  const [rows, setRows] = useState([]);

  const refresh = (status) =>
    api
      .get(`/approvals?status=${status}`)
      .then((r) => setRows(r.data))
      .catch(() => setRows([]));

  useEffect(() => {
    refresh(tab);
  }, [tab]);

  const decide = async (id, action) => {
    try {
      await api.post(`/approvals/${id}/${action}`);
      toast.success(`Request ${action}d`);
      refresh(tab);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  const isChecker = user?.role === "super_admin";
  const subtitle = isChecker
    ? "Review and approve sensitive actions raised by hostel and school admins."
    : "Track the status of sensitive actions you have raised. Awaits super-admin review.";

  return (
    <DashboardLayout title="Maker-Checker Approvals" subtitle={subtitle}>
      <div className="vs-card p-4 mb-4 flex items-start gap-3 text-sm">
        <ShieldCheck size={20} weight="duotone" className="text-[var(--vs-secondary)] mt-0.5" />
        <div className="text-[var(--vs-text-secondary)]">
          <span className="font-medium text-[var(--vs-text)]">4-eyes principle.</span>{" "}
          Sensitive actions (currently: certificate issuance) raised by hostel/school admins are
          held for super-admin approval. Super-admin actions execute immediately.
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="pending" data-testid="approvals-tab-pending">Pending</TabsTrigger>
          <TabsTrigger value="approved" data-testid="approvals-tab-approved">Approved</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="approvals-tab-rejected">Rejected</TabsTrigger>
        </TabsList>
        <TabsContent value={tab}>
          <div className="vs-card overflow-x-auto mt-4">
            <table className="w-full text-sm">
              <thead className="bg-[var(--vs-bg-subtle)]">
                <tr className="text-left text-[11px] uppercase font-mono tracking-widest text-[var(--vs-text-secondary)]">
                  <th className="px-4 py-3">Action</th>
                  <th className="px-4 py-3">Summary</th>
                  <th className="px-4 py-3">Requested by</th>
                  <th className="px-4 py-3">When</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`approval-row-${r.id}`} className="border-t border-[var(--vs-border)]">
                    <td className="px-4 py-3 font-mono text-xs">{r.action}</td>
                    <td className="px-4 py-3 text-xs max-w-md">{r.summary}</td>
                    <td className="px-4 py-3 font-mono text-xs">{r.requested_by_email}</td>
                    <td className="px-4 py-3 font-mono text-xs">{new Date(r.requested_at).toLocaleString()}</td>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className={STATUS_STYLE[r.status]}>
                        {r.status === "pending" && <Clock size={11} className="inline mr-1" />}
                        {r.status === "approved" && <CheckCircle size={11} className="inline mr-1" />}
                        {r.status === "rejected" && <XCircle size={11} className="inline mr-1" />}
                        {r.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 flex gap-2">
                      {r.status === "pending" && isChecker && (
                        <>
                          <Button
                            size="sm"
                            onClick={() => decide(r.id, "approve")}
                            data-testid={`approve-${r.id}`}
                            className="bg-[var(--vs-success)] hover:opacity-90 text-white"
                          >
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => decide(r.id, "reject")}
                            data-testid={`reject-${r.id}`}
                          >
                            Reject
                          </Button>
                        </>
                      )}
                      {r.status === "approved" && r.result?.cert_no && (
                        <span className="text-[11px] font-mono text-[var(--vs-success)]">{r.result.cert_no}</span>
                      )}
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-10 text-center text-[var(--vs-text-secondary)]">
                      No {tab} requests.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </DashboardLayout>
  );
}
